import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { subDays, subHours } from 'date-fns';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';

export function isRegistrationNotYetOpen(
  timeframe: Maybe<Pick<Timeframe, 'from' | 'isRegistrationPossibleBesidesTimeframe'>>,
  now: Date = new Date(),
): boolean {
  return !!timeframe && timeframe.isRegistrationPossibleBesidesTimeframe !== true && timeframe.from > now;
}

export function inAutomaticMoveUpBlockPeriod(
  product: NarrowedEntityData<
    Workshop,
    {
      appointments: { start: 1 };
      settings: {
        blockAutomaticMoveUpDaysInAdvance: 1;
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
      };
    }
  >,
  now: Date = new Date(),
): boolean {
  const settings = product.settings;
  const blockDays = Number(
    settings.blockAutomaticMoveUpDaysInAdvance ?? settings.inheritedBlockAutomaticMoveUpDaysInAdvance?.[1],
  );
  const startTime = product.appointments[0]?.start;
  return !!blockDays && !!startTime && now > subDays(startTime, blockDays);
}

function lowPriorityReleaseReached(product: Parameters<typeof lowPriorityMayActivelyRegister>[0], now: Date): boolean {
  const settings = product.settings;
  const blockDays = Number(
    settings.blockAutomaticMoveUpDaysInAdvance ?? settings.inheritedBlockAutomaticMoveUpDaysInAdvance?.[1],
  );
  if (blockDays) return inAutomaticMoveUpBlockPeriod(product, now);

  // check low-priority blockfrist
  if (product.nachrueckBlockTimeForLowPriorityInDays || product.nachrueckBlockTimeForLowPriorityInDays === 0) {
    const startTime = product.appointments[0]?.start;
    const endOfRegistrationBlock = startTime
      ? subDays(startTime, product.nachrueckBlockTimeForLowPriorityInDays)
      : null;
    if (!endOfRegistrationBlock) return false; // no start date yet => dont let in
    if (now < endOfRegistrationBlock) return false; // block period not over yet
  }

  return true;
}

export function nachrueckPossible(
  participation: NarrowedEntityData<
    ParticipationWorkshop,
    {
      _id: 1;
      state: 1;
      userID: 1;
      nachrueckPriority: 1;
      product: {
        _class: 1;
        institutionID: 1;
        isCanceled: 1;
        leftCapacity: 1;
        nachrueckBlockTimeForLowPriorityInDays: 1;
        requireManualAdmission: 1;
        appointments: { start: 1; end: 1 };
        settings: {
          blockAutomaticMoveUpDaysInAdvance: 1;
          inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
          blockActiveRegistrationHoursInAdvance: 1;
          inheritedBlockActiveRegistrationHoursInAdvance: 1;
        };
      };
    }
  >,
  now: Date = new Date(),
  options: { mode?: 'automatic' | 'active' | 'release' } = {},
) {
  logger.info({ participation }, 'check nachrueckPossible');
  if (participation.state !== ParticipationState.waiting) return false;
  const product = participation.product;
  if (!product) throw new Error('product not set');
  if (product.requireManualAdmission) return false;
  if (typeof product.leftCapacity !== 'number') throw new Error('product.leftCapacity is not a number');
  if (product.leftCapacity <= 0) return false;
  if (product.isCanceled) return false;
  if (participation.product?._class !== 'Workshop') {
    throw new Error('nachrueckPossible only works for workshops');
  }

  // check last termin
  const startOfLastTermin = product.appointments[product.appointments.length - 1]?.start;
  if (startOfLastTermin) {
    if (now > startOfLastTermin) return false; // event is already over
  }

  // check blockAutomaticMoveUpDaysInAdvance
  if (inAutomaticMoveUpBlockPeriod(product, now)) {
    if (options.mode === 'active') {
      if (!canActivelyRegisterForProduct(product, now)) return false;
      // fall through to priority checks
    } else if (options.mode !== 'release') {
      return false;
    }
  }

  if (participation.nachrueckPriority === 'high') return true;
  if (participation.nachrueckPriority === 'entry_requirements_not_met') return false;
  if (participation.nachrueckPriority !== 'low')
    throw new Error('unknown priority value: ' + participation.nachrueckPriority);

  return lowPriorityReleaseReached(product, now);
}

export function getNachruecker(
  workshop: NarrowedEntityData<
    Workshop,
    {
      _id: 1;
      institutionID: 1;
      leftCapacity: 1;
      settings: {
        autoNachrueckenEnabled: 1;
        blockAutomaticMoveUpDaysInAdvance: 1;
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
        blockActiveRegistrationHoursInAdvance: 1;
        inheritedBlockActiveRegistrationHoursInAdvance: 1;
      };
    }
  >,
  participations: (NarrowedEntityData<
    ParticipationWorkshop,
    {
      _id: 1;
      state: 1;
      userID: 1;
      nachrueckPriority: 1;
      waitingListPlace: 1;
      product: {
        _class: 1;
        isCanceled: 1;
        institutionID: 1;
        leftCapacity: 1;
        nachrueckBlockTimeForLowPriorityInDays: 1;
        requireManualAdmission: 1;
        appointments: { start: 1; end: 1 };
        settings: {
          blockAutomaticMoveUpDaysInAdvance: 1;
          inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
          blockActiveRegistrationHoursInAdvance: 1;
          inheritedBlockActiveRegistrationHoursInAdvance: 1;
        };
      };
    }
  > & {
    productPriceForUser?: Maybe<{ unitAmount: number }>;
  })[],
  options: { releasePass?: boolean } = {},
) {
  logger.info({ workshop }, 'getNachruecker');
  // sort participations by waitingListPlace
  participations.sort((p1, p2) => (p1?.waitingListPlace ?? 99999) - (p2?.waitingListPlace ?? 99999));

  const canAutoNachruecken = [] as string[];
  const canNotAutoNachruecken = [] as typeof participations;

  for (const participation of participations) {
    if (!participation.product) throw new Error('participation has no product');
    Object.assign(participation.product, { settings: workshop.settings });

    const autoNachrueckPossible =
      nachrueckPossible(participation, new Date(), { mode: options.releasePass ? 'release' : 'automatic' }) &&
      canAutoNachruecken.length < workshop.leftCapacity &&
      workshop.settings.autoNachrueckenEnabled &&
      (!participation.productPriceForUser || !participation.productPriceForUser.unitAmount); // when workshop cost money, manuell nackruecken is required

    if (autoNachrueckPossible) canAutoNachruecken.push(participation._id);
    else canNotAutoNachruecken.push(participation);
  }

  const canManuallyNachruecken = [] as string[];
  const stillOnWaitinglist = [] as string[];
  for (const participation of canNotAutoNachruecken) {
    if (!participation.product) throw new Error('participation has no product');
    Object.assign(participation.product, { settings: workshop.settings });

    const manuallNachrueckPossible =
      nachrueckPossible(participation, new Date(), { mode: 'active' }) &&
      canAutoNachruecken.length < workshop.leftCapacity; // when auto nachruecken took all spots manuall nachrueck is not posssible anymore

    if (manuallNachrueckPossible) canManuallyNachruecken.push(participation._id);
    else stillOnWaitinglist.push(participation._id);
  }

  return {
    canManuallyNachruecken,
    canAutoNachruecken,
    stillOnWaitinglist,
  };
}

export const participationCountsAsParallelSignup = (
  participation: NarrowedEntity<
    Participation,
    { state: 1; product: { _class: 1; appointments: { _id: 1; end: 1 }; slotConfig: { end: 1 } } }
  >,
  now = new Date(),
) => {
  logger.info({ participation }, 'participationCountsAsParallelSignup');
  const state = participation.state;
  if (![ParticipationState.waiting, ParticipationState.in].includes(state)) return false;

  const endDate = (() => {
    const product = participation.product;
    if (product?._class === Workshop._class) {
      const _product = product as Workshop;
      return _product.appointments?.at(-1)?.end;
    }

    if (product?._class === Einzelberatung._class) {
      const _product = product as Einzelberatung;
      return _product.slotConfig?.end;
    }
  })();

  if (!endDate) return false;

  if (endDate <= now) return false;

  return true;
};

/**
 * Checks if a participation has any appointment that lies in the future.
 * For Workshops: checks if ANY appointment has a start date in the future
 * For Einzelberatung: checks if the specific timeslot for this participation is in the future
 */
export const participationHasFutureAppointment = (
  participation: NarrowedEntityData<
    Participation,
    {
      _class: 1;
      timeslotID: 1;
      product: {
        _class: 1;
        appointments: { start: 1 };
        slots: { _id: 1; start: 1 };
      };
    }
  >,
  now = new Date(),
): boolean => {
  const product = participation.product;
  if (!product) return false;

  if (product._class === Workshop._class) {
    // For workshops, check if ANY appointment is in the future
    const _product = product as Workshop;
    return _product.appointments?.some(apt => apt.start && apt.start > now) ?? false;
  }

  if (product._class === Einzelberatung._class) {
    // For Einzelberatung, check the specific timeslot for this participation
    const _product = product as Einzelberatung;
    const _participation = participation as ParticipationEinzelberatung;
    const timeslot = _product.slots?.find(slot => slot._id === _participation.timeslotID);
    return timeslot?.start ? timeslot.start > now : false;
  }

  return false;
};

/**
 * Validates if a user can register for a product based on blockSignInHoursInAdvance setting.
 *
 * @param product - Workshop or Einzelberatung with appointments/slots and settings
 * @param now - Current time (injectable for testing)
 * @returns true if registration is allowed, false if registration deadline has passed
 */
export function canSignInForProduct(
  product:
    | NarrowedEntityData<
        Workshop,
        {
          _class: 1;
          appointments: { start: 1 };
          settings: {
            blockSignInHoursInAdvance: 1;
          };
        }
      >
    | NarrowedEntityData<
        Einzelberatung,
        {
          _class: 1;
          slotConfig: { start: 1 };
          settings: {
            blockSignInHoursInAdvance: 1;
          };
        }
      >,
  now: Date = new Date(),
): boolean {
  const blockSignInHours = product.settings.blockSignInHoursInAdvance ?? 0;

  // No blocking if setting is 0 or not configured
  if (blockSignInHours <= 0) {
    return true;
  }

  let relevantStartTime: Date | undefined | null;

  // Workshop: Find earliest appointment
  if (product._class === Workshop._class) {
    const workshopProduct = product as Workshop;
    const earliestAppointment = workshopProduct.appointments
      ?.filter(a => a.start)
      .sort((a, b) => a.start!.getTime() - b.start!.getTime())[0];

    relevantStartTime = earliestAppointment?.start;
  }

  // Einzelberatung: Find specific timeslot
  if (product._class === Einzelberatung._class) {
    const einzelberatungProduct = product as Einzelberatung;
    relevantStartTime = einzelberatungProduct.slotConfig?.start;
  }

  // If no start time found, allow registration (edge case: TBD appointments)
  if (!relevantStartTime) {
    return true;
  }

  // Calculate deadline and check
  const registrationDeadline = subHours(relevantStartTime, blockSignInHours);

  return now < registrationDeadline;
}

/**
 * Validates if a user can actively register for a product (direct enrollment for free spots),
 * independent of the passive nachrücken system.
 *
 * @param product - Workshop with appointments and blockActiveRegistrationHoursInAdvance setting
 * @param now - Current time (injectable for testing)
 * @returns true if active registration is allowed, false if deadline has passed
 */
export function canActivelyRegisterForProduct(
  product: NarrowedEntityData<
    Workshop,
    {
      appointments: { start: 1; end: 1 };
      settings: {
        blockActiveRegistrationHoursInAdvance: 1;
        inheritedBlockActiveRegistrationHoursInAdvance: 1;
      };
    }
  >,
  now: Date = new Date(),
): boolean {
  const settings = product.settings;
  const configuredBlockHours =
    settings.blockActiveRegistrationHoursInAdvance ?? settings.inheritedBlockActiveRegistrationHoursInAdvance?.[1];
  const blockHours = configuredBlockHours == null ? null : Number(configuredBlockHours);

  const firstAppointment = product.appointments
    ?.filter(a => a.start)
    .sort((a, b) => a.start!.getTime() - b.start!.getTime())[0];

  // When setting is null/undefined (disabled): allow until end of first appointment
  if (blockHours == null) {
    if (!firstAppointment?.end) return true;
    return now < firstAppointment.end;
  }

  // When setting is a number: block X hours before first appointment start
  if (!firstAppointment?.start) return true;

  const deadline = subHours(firstAppointment.start, blockHours);
  return now < deadline;
}

export function lowPriorityMayActivelyRegister(
  product: NarrowedEntityData<
    Workshop,
    {
      appointments: { start: 1; end: 1 };
      nachrueckBlockTimeForLowPriorityInDays: 1;
      settings: {
        blockAutomaticMoveUpDaysInAdvance: 1;
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
        blockActiveRegistrationHoursInAdvance: 1;
        inheritedBlockActiveRegistrationHoursInAdvance: 1;
      };
    }
  >,
  now: Date = new Date(),
): boolean {
  return lowPriorityReleaseReached(product, now) && canActivelyRegisterForProduct(product, now);
}

export function getParticipationTimespan(
  participation:
    | NarrowedEntityData<ParticipationWorkshop, { _class: 1; product: { appointments: { start: 1; end: 1 } } }>
    | NarrowedEntityData<
        ParticipationEinzelberatung,
        { _class: 1; product: { slots: { start: 1; end: 1 } }; timeslotID: 1 }
      >,
) {
  if (participation._class === ParticipationEinzelberatung._class) {
    const p = participation as ParticipationEinzelberatung;
    const slot = p.product!.slots.find(s => s._id === p.timeslotID);
    if (!slot) throw new Error('slot not found');
    return { start: slot.start, end: slot.end };
  } else if (participation._class === ParticipationWorkshop._class) {
    let start = null as Date | null;
    let end = null as Date | null;
    const p = participation as ParticipationWorkshop;
    for (const appointment of p.product!.appointments) {
      if (appointment.start && (!start || start > appointment.start)) {
        start = appointment.start;
      }
      if (appointment.end && (!end || end < appointment.end)) {
        end = appointment.end;
      }
    }
    return { start, end };
  }

  throw 'invalid _class: ' + participation._class;
}
